import { Repeat, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

const DOW = [
  { i: 0, l: "Su" }, { i: 1, l: "Mo" }, { i: 2, l: "Tu" }, { i: 3, l: "We" },
  { i: 4, l: "Th" }, { i: 5, l: "Fr" }, { i: 6, l: "Sa" },
];

const NTH = ["1st", "2nd", "3rd", "4th", "5th"];
const ord = (n) => n + ((n % 100 > 10 && n % 100 < 14) ? "th" : ["th", "st", "nd", "rd"][n % 10] || "th");
const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const fmtD = (d) => new Date(`${d}T12:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });

// Live list of the dates a pattern will create, from the same engine the server uses.
function DatesPreview({ startDate, rule, testid }) {
  const [res, setRes] = useState(null);
  const key = JSON.stringify(rule);
  useEffect(() => {
    if (!startDate || !rule) { setRes(null); return undefined; }
    const t = setTimeout(() => {
      api.post("/events/recurrence-preview", { date: startDate, recurrence: rule }).then((r) => setRes(r.data)).catch(() => setRes(null));
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, key]);
  if (!startDate) return <p className="text-[11px] text-[#B5794E]">Pick the first date above to see every date this creates.</p>;
  if (!res) return null;
  return (
    <div data-testid={`${testid}-preview`} className="rounded-md bg-white border border-[#E5E0D8] p-2">
      <p className="text-xs font-medium text-[#2A3942] mb-1">{res.count} date{res.count === 1 ? "" : "s"} will be created{res.capped ? " (maximum reached)" : ""}:</p>
      <ul className="text-[11px] text-[#4A5D6B] grid grid-cols-2 gap-x-3 max-h-28 overflow-y-auto">
        {res.dates.map((d) => <li key={d}>{fmtD(d)}</li>)}
      </ul>
    </div>
  );
}

// value: null (does not repeat) OR { freq, interval, weekdays[], count, until, monthly_by, nth, dates[] }
export default function RecurrencePicker({ value, onChange, testid = "recurrence", startDate = "" }) {
  const rule = value && value.freq ? value : null;
  const ruleRef = useRef(rule);
  ruleRef.current = rule; // keep the latest emitted rule for atomic merges

  const freq = rule?.freq || "none";
  const interval = rule?.interval || 1;
  const weekdays = Array.isArray(rule?.weekdays) ? rule.weekdays : [];
  const endMode = rule?.until ? "until" : "count";
  const count = rule?.count ?? 8;
  const until = rule?.until || "";
  // "Bi-weekly" is weekly with interval 2 — surface it as its own dropdown choice.
  const selectVal = freq === "weekly" && interval === 2 ? "biweekly" : freq;

  const emit = (next) => { ruleRef.current = next; onChange(next); };

  // merge a patch against the LATEST rule (ruleRef), not a stale render closure
  const set = (patch) => {
    const c = ruleRef.current || { freq: "weekly", interval: 1, weekdays: [], count: 8, until: "" };
    emit({
      freq: c.freq, interval: c.interval || 1, weekdays: c.weekdays || [],
      count: c.count ?? 8, until: c.until || "",
      ...(c.monthly_by ? { monthly_by: c.monthly_by, nth: c.nth } : {}), ...(c.dates ? { dates: c.dates } : {}), ...patch,
    });
  };

  const changeFreq = (f) => {
    if (f === "none") { emit(null); return; }
    const c = ruleRef.current || {};
    if (f === "biweekly") {
      emit({ freq: "weekly", interval: 2, weekdays: c.weekdays || [], count: 8, until: "" });
      return;
    }
    if (f === "custom") { emit({ freq: "custom", dates: c.dates || [] }); return; }
    emit({ freq: f, interval: 1, weekdays: f === "weekly" ? (c.weekdays || []) : [], count: 8, until: "", ...(f === "monthly" ? { monthly_by: "day" } : {}) });
  };

  const toggleDay = (i) => {
    const wd = (ruleRef.current?.weekdays) || [];
    set({ weekdays: wd.includes(i) ? wd.filter((d) => d !== i) : [...wd, i].sort((a, b) => a - b) });
  };

  const unitLabel = freq === "daily" ? "day" : freq === "monthly" ? "month" : "week";
  const inputCls = "rounded-lg border border-[#E5E0D8] px-2.5 py-1.5 text-sm outline-none focus:border-[#144F75] bg-white";

  return (
    <div data-testid={`field-${testid}`} className="rounded-lg border border-[#E5E0D8] p-3 bg-[#F9F7F3] space-y-3">
      <div className="flex items-center gap-2">
        <Repeat size={15} className="text-[#144F75]" />
        <select
          data-testid={`${testid}-freq`}
          value={selectVal}
          onChange={(e) => changeFreq(e.target.value)}
          className={`${inputCls} flex-1`}
        >
          <option value="none">Does not repeat</option>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="biweekly">Bi-weekly</option>
          <option value="monthly">Monthly</option>
          <option value="custom">Specific dates</option>
        </select>
      </div>

      {rule && freq === "custom" && (
        <div className="space-y-2" data-testid={`${testid}-custom`}>
          <p className="text-xs text-[#4A5D6B]">The first date above is included. Add any other dates:</p>
          <div className="flex flex-wrap gap-1.5">
            {(rule.dates || []).map((d) => (
              <span key={d} className="inline-flex items-center gap-1 text-xs bg-white border border-[#E5E0D8] rounded-full px-2 py-1">{fmtD(d)}
                <button type="button" aria-label={`Remove ${d}`} onClick={() => set({ dates: rule.dates.filter((x) => x !== d) })} data-testid={`${testid}-custom-remove-${d}`}><X size={11} /></button>
              </span>
            ))}
          </div>
          <input type="date" data-testid={`${testid}-custom-add`} className={inputCls} value=""
            onChange={(e) => e.target.value && set({ dates: [...new Set([...(rule.dates || []), e.target.value])].sort() })} />
          <DatesPreview startDate={startDate} rule={rule} testid={testid} />
        </div>
      )}

      {rule && freq !== "custom" && (
        <>
          <div className="flex items-center gap-2 text-sm text-[#2A3942]">
            <span>Every</span>
            <input
              type="number" min={1} data-testid={`${testid}-interval`}
              value={interval}
              onChange={(e) => set({ interval: Math.max(1, Number(e.target.value)) })}
              className={`${inputCls} w-16`}
            />
            <span>{unitLabel}{interval > 1 ? "s" : ""}</span>
          </div>

          {freq === "weekly" && (
            <div className="flex flex-wrap gap-1.5" data-testid={`${testid}-weekdays`}>
              {DOW.map((d) => (
                <button
                  type="button" key={d.i}
                  data-testid={`${testid}-dow-${d.i}`}
                  onClick={() => toggleDay(d.i)}
                  className={`h-8 w-8 rounded-full text-xs font-medium transition-colors ${
                    weekdays.includes(d.i) ? "bg-[#144F75] text-white" : "bg-white border border-[#E5E0D8] text-[#4A5D6B]"
                  }`}
                >
                  {d.l}
                </button>
              ))}
            </div>
          )}

          {freq === "monthly" && (
            <div className="flex flex-col gap-1.5 text-sm text-[#2A3942]" data-testid={`${testid}-monthly-by`}>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name={`${testid}-mby`} checked={rule.monthly_by !== "weekday"} onChange={() => set({ monthly_by: "day", nth: undefined })} className="accent-[#144F75]" data-testid={`${testid}-mby-day`} />
                On the same date{startDate ? ` (the ${ord(Number(startDate.slice(8, 10)))})` : ""}
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name={`${testid}-mby`} checked={rule.monthly_by === "weekday"} onChange={() => set({ monthly_by: "weekday" })} className="accent-[#144F75]" data-testid={`${testid}-mby-weekday`} />
                On the
                <select value={rule.nth || (startDate ? Math.floor((Number(startDate.slice(8, 10)) - 1) / 7) + 1 : 1)} disabled={rule.monthly_by !== "weekday"}
                  onChange={(e) => set({ monthly_by: "weekday", nth: Number(e.target.value) })} className={`${inputCls} disabled:opacity-50`} data-testid={`${testid}-nth`}>
                  {NTH.slice(0, 4).map((n, i) => <option key={n} value={i + 1}>{n}</option>)}
                  <option value={-1}>last</option>
                </select>
                {startDate ? DAY_NAMES[new Date(`${startDate}T12:00`).getDay()] : "weekday"}
              </label>
              {rule.monthly_by === "weekday" && <p className="text-[11px] text-[#8FA89B] pl-6">If the first date isn't on this weekday pattern, the series starts at the next matching date — check the list below.</p>}
            </div>
          )}

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-[#2A3942] cursor-pointer">
              <input
                type="radio" name={`${testid}-end`} checked={endMode === "count"}
                onChange={() => set({ count: count || 8, until: "" })}
                className="accent-[#144F75]"
              />
              <span>Ends after</span>
              <input
                type="number" min={1} data-testid={`${testid}-count`}
                value={count}
                disabled={endMode !== "count"}
                onChange={(e) => set({ count: Math.max(1, Number(e.target.value)), until: "" })}
                className={`${inputCls} w-16 disabled:opacity-50`}
              />
              <span>occurrences</span>
            </label>
            <label className="flex items-center gap-2 text-sm text-[#2A3942] cursor-pointer">
              <input
                type="radio" name={`${testid}-end`} checked={endMode === "until"}
                onChange={() => set({ until: until || new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10), count: null })}
                className="accent-[#144F75]"
              />
              <span>Ends on</span>
              <input
                type="date" data-testid={`${testid}-until`}
                value={until}
                disabled={endMode !== "until"}
                onChange={(e) => set({ until: e.target.value, count: null })}
                className={`${inputCls} disabled:opacity-50`}
              />
            </label>
          </div>
          <DatesPreview startDate={startDate} rule={rule} testid={testid} />
          <p className="text-[11px] text-[#8FA89B]">
            Creates one event per occurrence — each keeps its own capacity, registrations, and calendar sync.
          </p>
        </>
      )}
    </div>
  );
}
